// Pure plan-tier helpers — dependency-free so unit tests don't need to
// drag in `window`, React, or the auth context.
//
// Used by:
//   - PlanChangeRequestModal: decides which tile is self-serve vs Contact-sales.
//   - AIUsageSlideOver + ApiKeysTab: gate the Upgrade CTA on PLAN_REQUEST_ROLES.
//   - (Add new callers here as we add plan-aware flows.)

import type { SystemRole } from "./rbac";

export type PlanTier = "launch" | "free" | "growth" | "scale" | "enterprise";

/**
 * The canonical name for a stored tier. Mirrors canonicalPlanTier in @fastn/db:
 * `orgs.plan` carries both `free` and `launch` for the same tier until the
 * rename migration runs, so every tier comparison normalises here rather than
 * spelling out `=== "launch" || === "free"` at each branch.
 */
export function canonicalTier(tier: string | null | undefined): PlanTier {
  const t = (tier ?? "").toLowerCase();
  return (t === "free" ? "launch" : t) as PlanTier;
}

/** True for the free entry tier, under either of its names. */
export function isEntryTier(tier: string | null | undefined): boolean {
  return canonicalTier(tier) === "launch";
}

/** The one tier with a price in Stripe, so the one tier bought by card. */
export const CHECKOUT_TIER: PlanTier = "scale";

/**
 * Whether the org can buy CHECKOUT_TIER through Stripe Checkout right now,
 * rather than requesting it. Shared by the billing page and the plan modal: the
 * modal predates Stripe and kept sending Scale through request/approve, so the
 * header's Upgrade said "Request Scale" while the billing page took a card.
 *
 * Only from the entry tier, and only while no subscription is already taking
 * money — the API refuses a second one with 409 either way.
 */
export function canStartCheckout(
  tier: string | null | undefined,
  subscriptionStatus: string | null | undefined,
): boolean {
  return isEntryTier(tier) && !isCheckoutPaidPendingPlan(tier, subscriptionStatus);
}

/**
 * Paid, but the org row still says the entry tier: the webhook that applies the
 * plan has not landed yet. Without this the modal offered "Request Scale" to a
 * customer Stripe was already charging for it.
 */
export function isCheckoutPaidPendingPlan(
  tier: string | null | undefined,
  subscriptionStatus: string | null | undefined,
): boolean {
  return (
    isEntryTier(tier) &&
    (subscriptionStatus === "active" || subscriptionStatus === "trialing")
  );
}

/**
 * Roles allowed to submit a plan-change request. Must match the backend
 * (services/api/src/routes/plan-change-requests.ts uses requireRole("owner",
 * "admin"), which platform_admin also satisfies) — a CTA shown to anyone else
 * opens a modal whose submit 403s.
 */
export const PLAN_REQUEST_ROLES: SystemRole[] = ["platform_admin", "owner", "admin"];

/** Tier rank — used to compare current vs target. Higher = more expensive. */
export const TIER_RANK: Record<PlanTier, number> = {
  // `free` and `launch` are the same tier under two names and must rank the
  // same; without a `launch` entry TIER_RANK[tier] was undefined and every
  // isSelfServeChange comparison silently returned false.
  launch: 0,
  free: 0,
  growth: 1,
  scale: 2,
  enterprise: 3,
};

/**
 * Self-serve only when the target is a paid tier strictly above the current.
 *
 * - Upgrade to a higher paid tier (free->growth, growth->scale): self-serve.
 * - Downgrade (scale->growth, growth->free): NOT self-serve — Contact sales.
 *   Downgrades may involve billing pro-rates and data-retention implications
 *   we don't want a customer to trigger by accident.
 * - Enterprise (any direction): NOT self-serve — Contact sales.
 * - Same plan: NOT actionable (modal shows it as "Current" and disables it).
 */
export function isSelfServeChange(currentPlan: PlanTier, target: PlanTier): boolean {
  if (target === currentPlan) return false;
  if (target === "enterprise") return false;
  return TIER_RANK[target] > TIER_RANK[currentPlan];
}
